<template>
  <div class="content">
    <router-link class="btn btn-secondary" :to="{ path: '/dashboard' }">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-arrow-left"
           viewBox="0 0 16 16">
        <path fill-rule="evenodd"
              d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"/>
      </svg>
      Back to dashboard
    </router-link>

    <div v-if="loading" style="text-align: center">
      <b-spinner style="text-align: center" variant="primary" label="Loading..."></b-spinner>
    </div>

    <div v-if="data.data">
      <h3>Billing details</h3>
      <div v-if="data.company_name"><b>Company: {{ data.company_name }}</b></div>
      <div v-if="data.period">
        Period:
        <div class="row">
          <div class="col-lg-4 col-md-5">
            <v-select
              label="name"
              key="id"
              :options="DDL['periods']"
              v-model="formData['period']"
              @input="loadData"
              :reduce="option => option.id"
              :multiple="false"
              :clearSearchOnSelect="true"
              :closeOnSelect="true"
              :clearable="false"
            ></v-select>
          </div>
          <div class="col-md-2" v-if="deduct">
            <b-button
              style="width:90%"
              variant="primary"
              type="button"
              class="btn btn-warning btn-sm"
              @click="clearSearch()"
              v-b-modal.deductModal>
              Deduct leads
            </b-button>
          </div>
          <div class="col-md-6" v-if="checkedLeadsLength">
            <strong style="margin-right:10px">
              Selected to deduct from invoice: {{ checkedLeadsLength }}
              <span v-if="checkedLeadsLength>1">leads</span>
              <span v-if="checkedLeadsLength===1">lead</span>
            </strong>
            <b-button variant="primary" type="button" class="btn btn-warning btn-sm" @click="clearLeadsForDeduction()">
              Clear
            </b-button>
          </div>

          <div v-if="hasFutureFtds" class="col-md-9 ">
            <div class="float-right">
              <input type="checkbox" id="checkbox" v-model="showFutureFtds" @change="switchFutureFtds()">
              <label class="show-future-ftds-label" for="checkbox"> Show late FTDs from next periods</label>
            </div>

          </div>

        </div>
      </div>

      <div class="table-container">
        <b-table-simple
          v-if="!loading"
          class="billing-table table-scroll"
          style="margin-top: 20px; width: 100%; font-size: 14px"
          striped
          no-local-sorting
        >
          <b-thead>
            <b-th v-for="th in tableFields" :key="th.key">
              {{ th.label }}
            </b-th>
          </b-thead>
          <b-tbody>
            <b-tr v-for="country in reformBrokersList(data.data)" :key="country.id">
              <b-td v-if="country.sameBroker" :rowspan="country.sameBroker">
                {{ data.brokers[country.broker_id] }} (id: {{ country.broker_id }})
              </b-td>
              <b-td>
                {{ country.country.name }}
              </b-td>
              <b-td>
                {{ country.p1.length ? country.p1.join(', ') : '' }}
              </b-td>
              <b-td>
                <b-input type="number" v-on:input="updateCountryLeads(country)" v-model="country.leads"
                         :disabled="country.status.id !== 1 || country.valid_leads_disable"
                         :style="{width: '100px', height: '31px'}"
                />
              </b-td>
              <b-td>
                <span>{{ country.invalid_leads }}</span>
              </b-td>
              <b-td>
                <a target="_blank" v-bind:href="getLeadsLink(country)">{{ country.total_leads }}</a>
                <span v-if="country.summary_postponed && country.summary_postponed.leads > 0" class="orange-font">
                <br>(+ {{ country.summary_postponed.leads }})
              </span>
              </b-td>

              <b-td>
                <a target="_blank" v-bind:href="getFtdsLink(country)">{{ country.ftds }}</a>
                <span v-if="showFutureFtds && country.future_ftds && country.future_ftds > 0" class="green-font">
                  (+{{ country.future_ftds }})
                </span>

                <b-icon
                  v-if="showFutureFtds && country.future_ftds_tooltip != null && country.future_ftds_tooltip.length"
                  icon="exclamation-circle-fill"
                  variant="info"
                  data-toggle="tooltip"
                  data-html="true"
                  v-bind="showTooltip(country.future_ftds_tooltip)">
                </b-icon>

                <span v-if="country.summary_postponed && country.summary_postponed.leads > 0" class="orange-font">
                  <br>(+ {{ country.summary_postponed.ftds }})
                </span>
              </b-td>
              <b-td>
                <div
                  v-bind:class="(country.summary_postponed && country.summary_postponed.leads > 0) ? 'orange-font' : '' ">
                  {{ country.summary && country.summary.cr }}%
                </div>
              </b-td>
              <b-td>
                {{ country.crg }}%
              </b-td>
              <b-td>
                {{ country.comment }}
              </b-td>
              <b-td>
                ${{ country.payout }}
              </b-td>
              <b-td>
                <div
                  v-bind:class="(country.summary_postponed && country.summary_postponed.leads > 0) ? 'orange-font' : '' ">
                  <span v-html="country.calculation"></span>
                </div>
              </b-td>
              <b-td>
                <div>
                  <div class="form-group">
                    <b-input type="number" v-if="country.status.id === statuses.default"
                             v-model="country.adjustment"
                             @input="event => country.calculation = generateNewCalculation(country)"
                             :disabled="country.adjustment_disabled"
                             :style="{width: '100px', height: '31px'}"
                    />
                    <span v-else-if="country.adjustment">${{ country.adjustment }}</span>
                  </div>
                </div>
              </b-td>
              <b-td>
                <b-icon v-if="country.postponed_tooltip != null && country.postponed_tooltip.length"
                        icon="exclamation-circle-fill"
                        variant="info"
                        data-toggle="tooltip"
                        data-html="true"
                        v-bind="showTooltip(country.postponed_tooltip)">
                </b-icon>
                <span v-else>No</span>
              </b-td>
              <b-td>
                <v-select
                  :style="{margin: '5px 0'}"
                  v-if="country.status && (country.status.id === statuses.default || country.drafted_status === statuses.postponed) && data.editable && true"
                  class="fit"
                  label="name"
                  key="id"
                  v-model="dealStatus[country.broker_id][country.id]"
                  :options="statusesDropdown"
                  :multiple="false"
                  :clearSearchOnSelect="true"
                  :closeOnSelect="true"
                  :clearable="false"
                  :placeholder="'Select status'"
                  v-on:input="$event => changeStatus($event, country)"
                  v-bind:disabled="!!country.removed_leads"
                ></v-select>
                <span v-else>{{ getStatusName(country) }}</span>
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </div>


      <h3 class="orange-font" v-if="showlateFtds">Late ftds from previos period</h3>

      <div class="table-container">
        <b-table-simple
          v-if="showlateFtds"
          class="billing-table table-scroll">
          <b-thead>
            <b-th>
              Broker
            </b-th>
            <b-th>
              Country
            </b-th>
            <b-th>
              p1
            </b-th>
            <b-th>
              Period
            </b-th>
            <b-th>
              Email
            </b-th>
            <b-th>
              Internal User Id
            </b-th>
            <b-th>
              Payout
            </b-th>
            <b-th>
              Billing
            </b-th>
            <b-th>
              Description
            </b-th>
          </b-thead>
          <b-tbody>
            <b-tr v-for="late_ftd in reformList(data.late_ftds)" :key="late_ftd.internal_user_id">
              <b-td>
                {{ late_ftd.broker_name }} (id: {{ late_ftd.broker_id }})
              </b-td>
              <b-td>
                {{ late_ftd.country }}
              </b-td>
              <b-td>
                {{ late_ftd.p1 }}
              </b-td>
              <b-td>
              <span class="orange-font">
                {{ late_ftd.period_from }} - {{ late_ftd.period_to }}
              </span>
              </b-td>

              <b-td>
                {{ late_ftd.email }}
              </b-td>

              <b-td>
                <a target="_blank" v-bind:href="getUserIdLink(late_ftd.internal_user_id)">{{
                    late_ftd.internal_user_id
                  }}</a>
              </b-td>

              <b-td>
                {{ late_ftd.payout }}
              </b-td>

              <b-td>
                <span class="orange-font">
                {{ late_ftd.billing }}
                </span>
              </b-td>

              <b-td>
                <div>
                  <span class="orange-font">  {{ late_ftd.description }}</span>
                </div>
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </div>
    </div>

    <div class="buttons-block">
      <b-button class="btn-left" variant="warning" type="button" v-if="canDraft" @click="getCsvReport">Download report
        CSV
      </b-button>
      <b-button class="btn-left" variant="info" type="button" v-if="canDraft" @click="doSaveDraft">Save draft</b-button>
      <b-button variant="primary" type="button" @click="doCopy">Copy</b-button>
      <b-button v-if="data.editable"
                class="btn-right"
                type="button"
                :variant="applyButtonIsDisabled ? 'secondary' : 'success'"
                v-b-modal.invoiceModal
                @click="setDeductSummary">
        Apply
      </b-button>
    </div>

    <b-modal v-if="this.dataIsReady" ref="invoiceModal" id="invoiceModal" hide-footer size="lg" title="">

      <p class="h5 font-weight-bold">Invoicing:</p>
      <ul>
        <template v-for="brokerId in Object.keys(data.data)">
          <li v-if="brokerHasNewInvoice(brokerId)">
            <p><b>{{ data.brokers[brokerId] }} (id: {{ brokerId }}):</b></p>

            <div v-for="country in data.data[brokerId]">
              <p v-if="dealStatus[brokerId][country.id].id === statuses.invoiced">

                {{ country.country.name }}
                {{ country.p1.length ? ' (p1: ' + country.p1 + ' )' : '' }}:
                <span v-html="country.calculation"></span>

                <span v-if="country.missed_amount > 0">
                  <span v-if="country.adjustment > 0 || country.adjustment < 0">
                    <br>(Incl. manual adjustment: {{ country.sign }}${{ country.absAdjustment }})
                  </span>
                </span>
                <span v-else>no missings</span>
              </p>
            </div>
          </li>
        </template>
      </ul>
      <p v-if="deductSummary > 0">Total will be deducted from the balance: ${{ deductSummary | formatNumber }}</p>

      <p class="h5 font-weight-bold">Postponing:</p>
      <ul>
        <template v-for="brokerId in Object.keys(data.data)">
          <li v-if="brokerHasNewPostponed(brokerId)">
            <p><b>{{ data.brokers[brokerId] }} (id: {{ brokerId }}):</b></p>
            <div v-for="country in data.data[brokerId]">
              <p v-if="dealStatus[brokerId][country.id].id === statuses.postponed">
                {{ country.country.name }}
                {{ country.p1.length ? ' (p1: ' + country.p1 + ' )' : '' }}:
                <span v-html="country.calculation"></span>

                <span v-if="country.missing > 0">
                  FTDs missing: {{ country.missing | formatNumber }}, Payout: ${{ country.payout }}
                </span>
                <span v-else>no missings</span>
              </p>
            </div>
          </li>
        </template>
      </ul>

      <div class="btn-group float-right">
        <div class="btn-group mr-2">
          <b-button variant="primary" block
                    @click="$bvModal.hide('invoiceModal')">Cancel
          </b-button>
        </div>
        <div class="btn-group mr-2">
          <b-button variant="success" block @click="doSave">Save</b-button>
        </div>
      </div>
    </b-modal>

    <b-modal ref="deductModal" id="deductModal" hide-footer size="lg" title="">

      <template #modal-title>
        <b>Choose the leads for deduction</b>
      </template>
      <div class="buttons-block">
        <i class="nc-icon nc-zoom-split lens" style="right: 191px;"></i>
        <input type="text" class="mx-input col-md-2 btn-left" placeholder="Search by status"
               @keyup="searchByStatus($event.target.value)">

        <i class="nc-icon nc-zoom-split lens"></i>
        <input type="text" class="mx-input col-md-2" placeholder="Search by Email"
               @keyup="searchByEmail($event.target.value)">
      </div>
      <deduct-table
        v-for="(lead, i) in deductData"
        :key="lead.id"
        :leads="Object.values(lead)"
        :leadsChecked="checkedLeads[Object.keys(lead)[0]]"
        :deductTableFields="deductTableFields"
        :periodId="Object.keys(lead)[0]"
        :filtering="filtering"
        :postponed="i"
        @click="checkLead">
      </deduct-table>
      <div class="btn-group float-right" style="margin-top: 10px">
        <div class="btn-group mr-2">
          <b-button variant="primary" block @click="$bvModal.hide('deductModal')">Cancel</b-button>
        </div>
        <div class="btn-group mr-2">
          <b-button variant="success" block @click="doDeduct(); $bvModal.hide('deductModal')">Deduct</b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<style scoped>
.table tr td {
  vertical-align: middle;
}

.lens {
  position: absolute;
  right: 25px;
  z-index: 10
}

.rowspan {
  border-bottom: 4px solid #dee2e6;
}

</style>

<script>
import API from '../api/Api'
import vSelect from 'vue-select'
import Vue from "vue";
import {saveAs} from 'file-saver';

export default {
  components: {
    vSelect,
  },

  data: () => ({
    loading: false,
    deduct: false,
    id: null,
    from: null,
    to: null,
    adminURL: null,
    entityName: 'Billing',
    data: [],
    deductData: [],
    leadsData: {
      removedLeads: {},
    },
    dealStatus: {},
    weekend: null,
    checkedLeads: {},
    checkedLeadsLength: 0,
    filtering: false,
    tableFields: [
      {
        key: 'broker_id',
        label: 'Broker',
      },
      {
        key: 'country',
        label: 'Country',
      },
      {
        key: 'p1',
        label: 'p1',
      },
      {
        key: 'leads',
        label: 'Valid leads',
      },
      {
        key: 'invalid_leads',
        label: 'Invalid leads',
      },
      {
        key: 'total_leads',
        label: 'Total leads',
      },
      {
        key: 'ftds',
        label: 'FTDs'
      },
      {
        key: 'cr',
        label: 'CR',
      },
      {
        key: 'crg',
        label: 'CRG',
      },
      {
        key: 'comment',
        label: 'Comment',
      },
      {
        key: 'payout',
        label: 'Payout',
      },
      {
        key: 'calculation',
        label: 'Calculation',
      },
      {
        key: 'adjustment',
        label: 'Adjustment',
      },
      {
        key: 'postponed',
        label: 'Postponed',
      },
      {
        key: 'action',
        label: 'Status',
      },
    ],
    deductTableFields: [
      {
        key: 'checked',
        label: '',
      },
      {
        key: 'broker_id',
        label: 'Broker',
      },
      {
        key: 'country',
        label: 'Country',
      },
      {
        key: 'created_at',
        label: 'Reg. date',
      },
      {
        key: 'p1',
        label: 'p1',
      },
      {
        key: 'sale_status',
        label: 'Sale status'
      },
      {
        key: 'email',
        label: 'Email',
      },
      {
        key: 'ftd',
        label: 'FTD',
      }
    ],
    DDL: {},
    formData: {
      period: null
    },
    deductSummary: 0,
    postponedColor: "",
    statusesDropdown: [],
    statuses: {
      default: 1,
      postponed: 2,
      invoiced: 3,
    },
    applyButtonIsDisabled: false,
    dataIsReady: false,
    showlateFtds: false,
    showFutureFtds: false,
    hasFutureFtds: false,
    canDraft: false,
  }),
  mounted: function () {
    this.id = this.$route.params.id;
    this.from = this.$route.query.from;
    this.to = this.$route.query.to;
    this.loadDDL();
  },
  methods: {
    loadDDL() {
      this.loading = true;
      Promise.all([
        API.get(`/distribution/deals/${this.id}/periods`).then(data => {

          if (data.data.length) {
            this.DDL['periods'] = data.data;
            this.formData['period'] = this.DDL['periods'][0]['id'];
          }
        }),

        API.get(`distribution/deals/billing-statuses`).then(data => {
          this.statusesDropdown = data.data;
        }).catch(e => this.error = e.message),

      ]).then(() => {
        this.adminURL = Vue.prototype.config.adminURL
        this.loadData();
      })
        .catch(e => {
          this.error = e.message
          this.notify(e.message, 'warning')
          throw e

        })
        .finally(() => {
          this.loading = false;
        });
    },
    changeStatus: function (e, country) {

      /** Uncomment if you need to block changing valid/invalid leads
       *
       if (e.id === 3) {
       country.valid_leads_disable = false;
       } else if (e.id === 2) {
       country.valid_leads_disable = true;

       let total_leads = country.leads + country.invalid_leads;
       country.leads = country.original_leads;
       country.invalid_leads = total_leads - country.original_leads;
       }
       **/

      if (country.missed_amount > 0) {
        if (e.id === 3) {
          country.adjustment_disabled = false;
        }

        if (e.id === 2) {
          country.adjustment = 0;
          country.adjustment_disabled = true;
        }

        country.calculation = this.generateNewCalculation(country);
      }

      let brokerId = country.broker_id;
      let countryId = country.id;

      let status = Object.assign({}, this.dealStatus);
      if (e) {
        this.$set(status[brokerId], countryId, {id: e.id, name: e.name});
      } else {
        this.$set(status[brokerId], countryId, {id: 1, name: "Skip"});
      }
      this.dealStatus = status;
      this.deductData.map(item => Object.values(item).forEach(
        element => element.forEach(lead => {
          this.dealStatus[lead.broker_id][lead.id].id === 2 ? lead.postponed = true : lead.postponed = false;
        })
      ))
    },
    prepareTableData: function (response) {
      this.data = response.data;
      this.weekend = this.data.weekend

      if (Array.isArray(this.data.late_ftds) && this.data.late_ftds.length > 0) {
        this.showlateFtds = true
      }

      let hasFutureFtds = false
      this.canDraft = false

      Object.keys(response.data.data).forEach((id) => {
        this.dealStatus[id] = {};
        response.data.data[id].forEach(country => {

          if (country.future_ftds) {
            hasFutureFtds = true
          }

          if (country.status.id === this.statuses.default) {
            this.canDraft = true;

            this.dealStatus[id][country.id] = {id: 3, name: 'Invoice'};
          } else {
            this.dealStatus[id][country.id] = country.status;
          }

          country.adjustment_disabled = country.missed_amount <= 0;
          country.total_leads = country.leads + country.invalid_leads;
          country.original_leads = country.leads;
        })
      });
      1
      this.hasFutureFtds = hasFutureFtds
      this.dataIsReady = true;
    },
    getDraft: function () {
      this.loading = true;

      let dealData = JSON.parse(JSON.stringify(this.data));
      API.post(`distribution/deals/${this.id}/get-draft`, dealData).then(response => {
        const draftPeriodKeys = Object.keys(response.data).map(item => parseInt(item));


        Object.keys(this.data.data).forEach(broker_id => {

          this.data.data[broker_id].forEach((country, index) => {

            if (country.main_period_id && response.data[country.main_period_id]) {

              country.drafted_status = response.data[country.main_period_id].status;

              if (country.drafted_status === 2) {
                this.dealStatus[broker_id][country.id] = {
                  id: 2,
                  name: 'Postponed'
                }
              }

              country.adjustment = response.data[country.main_period_id].adjustment;

              let total_leads = country.leads + country.invalid_leads;
              country.leads = response.data[country.main_period_id].leads;
              country.invalid_leads = total_leads - country.leads;

              if (country.leads < 0) {
                country.summary.cr = 0;
              } else if (country.ftds > 0) {
                country.summary.cr = 100 * country.summary.ftds / (country.leads + country.summary_postponed.leads);
                country.summary.cr = Math.round(country.summary.cr * 100) / 100
              }

              this.updateCountryLeads(country);
              //country.calculation = this.generateNewCalculation(country)

            }
          })
        })

        if (draftPeriodKeys.length > 0) {
          this.notify('Draft loaded', 'success');
        } else {
          this.notify('Draft not found', 'warning');
        }

        //console.log( response.data);
      }).catch(e => this.error = e.message)
        .finally(() => {
          this.loading = false;
        });

    },
    loadData: function () {
      this.showlateFtds = false;
      this.loading = true;

      if (!this.DDL['periods']) {
        throw new Error('No active periods created yet')
      }

      let period = this.DDL['periods'].find(item => item['id'] === this.formData['period']);
      this.from = period['from']
      this.to = period['to']

      API.get(`distribution/deals/${this.id}/billing?from=${period['from']}&to=${period['to']}`).then(response => {
        //this.loadLeads();
        this.prepareTableData(response);
        this.getDraft();

        //console.log('hasFutureFtds', hasFutureFtds)
      }).catch(e => this.error = e.message)
        .finally(() => {
          this.loading = false;
        });
    },
    loadLeads: function () {
      let period = this.DDL['periods'].find(item => item['id'] === this.formData['period']);
      this.from = period['from']
      this.to = period['to']
      this.deductData = []
      API.get(`distribution/deals/${this.id}/leads?from=${period['from']}&to=${period['to']}`)
        .then(response => {
          response.data.forEach(period => {
            let periodName = period.period_from + '_' + period.period_to
            let leadsArray = [];
            period.data.forEach(element => {
              if (element.leads.length) {
                element.leads.forEach(lead => {
                  leadsArray.push({
                    broker_name: element.broker_name,
                    country_name: element.country_name,
                    created_at: lead.created_at,
                    id: element.id,
                    broker_status: lead.broker_status,
                    email: lead.email,
                    ftd: +lead.ftd,
                    p1: lead.p1,
                    postponed: this.dealStatus[element.broker_id][element.id] === 2 ? true : false,
                    broker_id: element.broker_id,
                  })
                })
              }
            })
            this.deductData.push({
              [periodName]: leadsArray
            })
          })
          this.deduct = this.reformBrokersList(this.data.data).some(item => item.status.id === 1);
          this.deductData.forEach(item => {
            item[Object.keys(item)[0]].forEach(lead => lead.filterExcluded = false)
          });
          this.deductData.forEach(item => this.checkedLeads[Object.keys(item)[0]] = []);
          this.deductData.sort((a, b) => new Date(Object.keys(b).toString().split("_")[0]).getTime() - new Date(Object.keys(a).toString().split("_")[0]).getTime());
        })
        .catch(e => console.log(e))
    },
    doSave: async function () {
      let notification = {type: null, message: 'ok'};
      let deal = JSON.parse(JSON.stringify(this.data));
      let postponeSelected = false;
      deal['removedLeads'] = this.leadsData.removedLeads;
      this.leadsData.removedLeads = {};

      Object.keys(this.dealStatus).forEach((brokerId) => {
        Object.keys(this.dealStatus[brokerId]).forEach((countryId) => {
          let countryIndex = deal.data[brokerId].findIndex((country) => +country.id === +countryId);
          if (countryIndex >= 0) {
            this.$set(deal.data[brokerId][countryIndex], 'status', this.dealStatus[brokerId][countryId]);

            if (this.dealStatus[brokerId][countryId].id === 2) {
              postponeSelected = true;
            }
          }
        })
      })

      // if (this.checkedLeadsLength > 0 &&  postponeSelected) {
      //   this.notify(` Leads cannot be deducted if period is postponed`, 'warning');
      //   return;
      // }

      API.post(`/distribution/deals/${this.id}/invoice`, deal)
        .then(response => {
          if (response.status === 200) {
            notification = {type: 'success', message: 'Success'};
          } else {
            notification = {type: 'warning', message: `Unable to invoice. Please try again.`};
          }
        }).catch(e => {
        notification = {type: 'danger', message: `Unable to invoice with error ${e.message}`}
      }).finally(async () => {
        if (notification.type === 'success') {
          // if success, dont reloda data, just redirect
          // await this.loadData();
          this.$bvModal.hide('invoiceModal')
          window.location.href = '/dashboard';
        }
        this.notify(notification.message, notification.type)
      });
    },
    doDeduct: function () {
      this.loading = true;
      this.dataIsReady = false;
      this.checkedLeadsLength = Object.values(this.checkedLeads).reduce((prev, next) => prev + next.length, 0);
      let periods = Object.keys(this.checkedLeads);
      periods.forEach(period => this.leadsData.removedLeads[period] = []);
      periods.forEach(period => this.checkedLeads[period].forEach(item => {
        const groupId = item.split('_')[0]
        if (this.leadsData.removedLeads[period].find(element => element.id === groupId)) {
          this.leadsData.removedLeads[period].forEach(period => {
            if (period.id === groupId) {
              period.leads_count = period.leads_count + 1
            }
          })
        } else {
          this.leadsData.removedLeads[period].push({
            id: groupId,
            leads_count: 1
          })
        }
      }))
      let period = this.DDL['periods'].find(item => item['id'] === this.formData['period']);
      this.from = period['from']
      this.to = period['to']

      API.post(`distribution/deals/${this.id}/billing?from=${period['from']}&to=${period['to']}`, this.leadsData).then(response => {
        this.prepareTableData(response);
      }).catch(e => this.error = e.message)
        .finally(() => {
          this.loading = false;
        });

    },
    showTooltip: function (text) {
      return {
        'title': text
      };
    },
    brokerHadInvoice: function (id) {
      return this.data.data[id].find(item => item.status.id === this.statuses.invoiced)
    },
    brokerHasNewInvoice: function (id) {
      return Object.values(this.dealStatus[id]).find(item => item.id === this.statuses.invoiced)
    },
    brokerHadPostponed: function (id) {
      return this.data.data[id].find(item => item.status.id === this.statuses.postponed)
    },
    brokerHasNewPostponed: function (id) {
      return Object.values(this.dealStatus[id]).find(item => item.id === this.statuses.postponed)
    },
    doCopy: function () {
      let str_invoiced = '';
      let str_postponed = '';
      let str_broker = '';
      let str_broker_postponed = '';

      let str_invoiced_broker_title = '';
      let str_postponed_broker_title = '';

      let rows_invoiced = 0;
      let rows_postponed = 0;
      let rows_broker_invoiced = 0;
      let rows_broker_postponed = 0;

      Object.keys(this.data.data).forEach(id => {

        if (this.brokerHadInvoice(id) || this.brokerHasNewInvoice(id)) {
          str_invoiced_broker_title = `${this.data.brokers[id]}\n`;
          rows_broker_invoiced = 0;
          str_broker = '';

          this.data.data[id].forEach((country, index) => {

            let leads = country.leads
            if (!leads && !country.summary.ftds) {
              return;
            }

            if (country.status.id === this.statuses.invoiced || this.dealStatus[id][country.id].id === this.statuses.invoiced) {
              rows_invoiced++;
              rows_broker_invoiced++;
              // str_broker += `${country.min_postponed} - ${this.data.period.to}\n`;
              str_broker += `${country.iso_code}\n`;

              // str += country.p1 ? ` \n` : `\n`; //TODO

              str_broker += this.generateNewCalculation(country).replace(/<[^>]*>?/gm, '');

              // ********* old code without using method generateNewCalculation(country)

              // str_broker += `Leads: ${leads}, FTDs: ${country.summary.ftds}, CR: ${country.cr}%, CRG: ${country.crg}%`;
              // if (country.missing > 0) {
              //   let result = country.missed_amount;
              //
              //   str_broker += `, FTDs missing: ${country.missing}\n`;
              //   str_broker += `${country.missing}*$${country.payout} = $${result}`;
              //
              //   if (country.adjustment && country.adjustment !== 0) {
              //     const final_amount = parseFloat(country.missed_amount) + parseFloat(country.adjustment);
              //     const abs_adjustment = Math.abs(country.adjustment);
              //     const sign = country.adjustment < 0 ? '-' : '+';
              //
              //     str_broker += `\nManual adjustment: ${sign}$${abs_adjustment}`;
              //     str_broker += `\nFinal amount: $${country.missed_amount} ${sign} $${abs_adjustment} = $${final_amount}`;
              //   }
              // } else {
              //   str_broker += `\nAll good`;
              // }

              str_broker += '\n\n';
            }
          });

          if (rows_broker_invoiced) {
            str_invoiced += str_invoiced_broker_title + str_broker;
          }
        }
      });

      Object.keys(this.data.data).forEach(id => {

        if (this.brokerHadPostponed(id) || this.brokerHasNewPostponed(id)) {

          rows_broker_invoiced = 0;
          str_postponed_broker_title = `${this.data.brokers[id]}\n`;

          this.data.data[id].forEach((country, index) => {

            let leads = country.leads
            if (!leads && !country.summary.ftds) {
              return;
            }

            if (country.status.id === this.statuses.postponed || this.dealStatus[id][country.id].id === this.statuses.postponed) {
              rows_postponed++;
              rows_broker_postponed++;
              str_broker_postponed += `${country.country.name} - `;
              str_broker_postponed += this.generateNewCalculation(country).replace(/<[^>]*>?/gm, '') + `\n\n`;
            }
          });

          if (rows_broker_postponed) {
            str_postponed += str_postponed_broker_title + str_broker_postponed;
          }
        }
      });

      let str_late_ftds = '';
      if (this.data.late_ftds.length) {
        str_late_ftds = '===== Late ftds from previos periods \n\n';

        Object.keys(this.data.late_ftds).forEach(index => {
          let period = this.data.late_ftds[index];
          str_late_ftds += 'Period: ' + period.period_from + ' - ' + period.period_to + '\n';
          str_late_ftds += 'Broker: ' + period.broker_name + ', country:  ' + period.country + ', Email: ' + period.email + 'User Id:' + period.internal_user_id + ', p1:  ' + period.p1 + '\n';
          str_late_ftds += 'payout: ' + period.payout + ', billing:  ' + period.billing + ', description:  ' + period.description + '\n\n';
        });
      }

      const el = document.createElement('textarea');

      let str = `\n`;  //`${this.data.company_name} \n\n`;

      if (rows_invoiced) {
        str += str_invoiced;
      }

      if (rows_postponed) {
        str += `==== Will wait for next week and calculate this amount:\n` + str_postponed;
      }

      if (!rows_invoiced && !rows_postponed) {
        str += `==== No any data with leads for period\n`;
      }

      if (this.data.late_ftds.length) {
        str += str_late_ftds;
      }

      el.value = str;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);

      this.notify('Copied successfully', 'success')
    },
    doSaveDraft: async function () {
      let notification = {type: null, message: 'ok'};
      let deal = JSON.parse(JSON.stringify(this.data));

      Object.keys(this.dealStatus).forEach((brokerId) => {
        Object.keys(this.dealStatus[brokerId]).forEach((countryId) => {
          let countryIndex = deal.data[brokerId].findIndex((country) => +country.id === +countryId);
          if (countryIndex >= 0) {
            this.$set(deal.data[brokerId][countryIndex], 'status', this.dealStatus[brokerId][countryId]);
          }
        })
      })

      API.post(`/distribution/deals/${this.id}/save-draft`, deal)
        .then(response => {
          if (response.status === 200) {
            notification = {type: 'success', message: 'Draft saved'};
          } else {
            notification = {type: 'warning', message: `Unable to save draft. Please try again.`};
          }
        }).catch(e => {
        notification = {type: 'danger', message: `Unable to invoice with error ${e.message}`}
      }).finally(async () => {
        this.notify(notification.message, notification.type);
        setTimeout(() => window.location.href = '/dashboard', 1000);
      });
    },
    getCsvReport: function () {
      let deal = JSON.parse(JSON.stringify(this.data));

      let period = this.DDL['periods'].find(item => item['id'] === this.formData['period']);
      this.from = period['from']
      this.to = period['to']

      API.post(`/distribution/deals/${this.id}/billing-csv?from=${period['from']}&to=${period['to']}`, deal)
        .then(response => {
          const blob = new Blob([response.data], {type: 'text/csv;charset=utf-8'});
          saveAs(blob, 'filename.csv');
        })
        .catch(error => {
          console.error('CSV loading error:', error);
        });
    },
    setDeductSummary: function () {
      let sum = 0;

      for (const key in this.data.data) {
        const broker = this.data.data[key];

        for (const country of broker) {
          if (country.status.id === 3 && !country.invoiced) {
            sum += country.payout * country.missing;
          }
        }
      }
      return this.deductSummary = sum;
    },
    formatNumber(num) {
      let dec = 2;
      let num_sign = num >= 0 ? 1 : -1;

      return parseFloat((Math.round((num * Math.pow(10, dec)) + (num_sign * 0.0001)) / Math.pow(10, dec)).toFixed(dec));
    },
    notify(msg, type) {
      this.$notifications.notify({
        message: msg,
        icon: 'nc-icon nc-app',
        horizontalAlign: 'right',
        verticalAlign: 'top',
        type: type
      });
    },
    // isPostponedExist: function (history) {
    //   return this.statusExist(this.statuses.postponed, history)
    // },
    // isInvoicedExist: function (history) {
    //   return this.statusExist(this.statuses.invoiced, history)
    // },
    // statusExist: function (status, history) {
    //   for (const brokerId of Object.keys(this.data.data)) {
    //     // if (status === this.statuses.invoiced && this.isRowInvoiced(this.data.data[brokerId], history)) {
    //     //   return true;
    //     // }
    //     //
    //     // if (status === this.statuses.postponed && this.isRowPostponed(this.data.data[brokerId], history)) {
    //     //   return true;
    //     // }
    //   }
    //   return false;
    // },
    // isRowInvoiced: function (country, history) {
    //   if (country.status.id === this.statuses.invoiced) {
    //     return true;
    //   }
    //
    //   return false;
    // },
    // isRowPostponed: function (country, history) {
    //   if (!history && country.status.id === this.statuses.postponed) {
    //     return true;
    //   }
    //
    //   if (history && country.status.id === this.statuses.postponed) {
    //     return true;
    //   }
    //
    //   return false;
    // },
    getStatusName: function (item) {
      if (item.status.id === this.statuses.postponed) {
        return 'Postponed';
      }
      if (item.status.id === this.statuses.invoiced) {
        if (item.was_postponed) {
          return 'Invoiced (Postponed)';
        }
        return 'Invoiced';
      }
      if (item.status.id === this.statuses.default) {
        return 'Auto postponed';
      }
      return '';
    },
    // multiplyNum: function (a, b) {
    //   a = this.formatNumber(a);
    //   b = this.formatNumber(b);
    //
    //   return a * b;
    // },
    round: function (a) {
      return a.toFixed(3)
    },

    reformBrokersList: function (list) {
      let reformedList = [];
      Object.keys(list).forEach(brokerId => {
        list[brokerId][0]['sameBroker'] = list[brokerId].length
      });
      reformedList = [].concat(...Object.values(list));
      let brokersP1 = {}
      reformedList.forEach(item => {
        if (item.p1.length >= 1) item.p1.forEach(p1 => {
          if (brokersP1[item.broker_id]) {
            if (brokersP1[item.broker_id][item.country_id]) {
              brokersP1[item.broker_id][item.country_id].push(p1)
            } else {
              brokersP1[item.broker_id][item.country_id] = [];
              brokersP1[item.broker_id][item.country_id].push(p1);
            }
          } else {
            brokersP1[item.broker_id] = {}
            brokersP1[item.broker_id][item.country_id] = [];
            brokersP1[item.broker_id][item.country_id].push(p1)
          }
        })
      })
      reformedList.forEach(item => {
        if (brokersP1[item.broker_id]) {
          item.brokersP1 = brokersP1[item.broker_id]
        }
      })
      return reformedList;
    },
    reformList: function (list) {
      let reformedList = [];
      reformedList = [].concat(...Object.values(list));
      return reformedList;
    },
    getLeadsLink: function (params) {
      let link = this.adminURL + 'statistic-reports/leads-and-ftds?is_demo=0'

      link += '&broker=' + params.broker_id;
      link += '&country=' + params.country_id;
      link += '&date_from=' + this.from;
      link += '&date_to=' + this.to;

      if (this.weekend) {
        link += '&is_weekend=0';
      }
      if (params.p1.length >= 1) {
        link += '&p1=' + params.p1.toString()
      } else if (params.p1.length < 1 && params.brokersP1) {
        if (params.brokersP1[params.country_id]) {
          link += '&exclude_p1=1'
          link += '&p1=' + params.brokersP1[params.country_id].toString()
        }
      }

      return link
    },
    getFtdsLink: function (params) {
      let link = this.adminURL + 'statistic-reports/leads-and-ftds?is_demo=0'

      link += '&broker=' + params.broker_id;
      link += '&broker_payout=' + params.payout;
      link += '&country=' + params.country_id;
      link += '&date_from=' + this.from;
      link += '&date_to=' + this.to;
      link += '&ftd_from=' + this.from;
      link += '&ftd_to=' + this.to;
      link += '&is_ftd=1';

      if (this.weekend) {
        link += '&is_weekend=0';
      }

      return link
    },
    getUserIdLink: function (internal_user_id) {
      return this.adminURL + 'statistic-reports/leads-and-ftds?user_id=' + internal_user_id
    },
    switchFutureFtds: function () {
      if (this.showFutureFtds) {
        console.log('Future FTDs are beeing shown')
      } else {
        console.log('Future FTDs showing is OFF')
      }
    },
    clearSearch() {
      this.filtering = false
      this.deductData.forEach(item => item[Object.keys(item)[0]].forEach(lead => lead.filterExcluded = false));
    },
    searchByEmail(text) {
      text = text.trim().toLowerCase();
      if (text) {
        this.deductData.forEach(item => {
          item[Object.keys(item)[0]].forEach(lead => {
            if (lead.email.toLowerCase().includes(text)) {
              lead.filterExcluded = false;
            } else {
              lead.filterExcluded = true;
            }
          })
        })
        this.filtering = true
      } else {
        this.filtering = false
        this.deductData.forEach(item => item[Object.keys(item)[0]].forEach(lead => lead.filterExcluded = false));
      }
    },
    searchByStatus(text) {
      text = text.trim().toLowerCase();
      if (text) {
        this.deductData.forEach(item => {
          item[Object.keys(item)[0]].forEach(lead => {
            if (lead.broker_status && lead.broker_status.toLowerCase().includes(text)) {
              lead.filterExcluded = false;
            } else {
              lead.filterExcluded = true;
            }
          })
        })
        this.filtering = true
      } else {
        this.filtering = false
        this.deductData.forEach(item => item[Object.keys(item)[0]].forEach(lead => lead.filterExcluded = false));
      }
    },
    clearLeadsForDeduction() {
      this.dataIsReady = false;
      this.loading = true;
      this.checkedLeadsLength = 0;
      this.leadsData.removedLeads = {};
      this.deductData.forEach(item => this.checkedLeads[Object.keys(item)[0]] = []);

      let period = this.DDL['periods'].find(item => item['id'] === this.formData['period']);
      this.from = period['from']
      this.to = period['to']

      API.get(`distribution/deals/${this.id}/billing?from=${period['from']}&to=${period['to']}`).then(response => {
        this.prepareTableData(response);
      }).catch(e => this.error = e.message)
        .finally(() => {
          this.loading = false;
        });
    },
    checkLead(value, periodId) {
      if (this.checkedLeads[periodId].includes(value)) {
        const index = this.checkedLeads[periodId].indexOf(value);
        this.checkedLeads[periodId].splice(index, 1)
      } else {
        this.checkedLeads[periodId].push(value)
      }
    },
    generateNewCalculation: function (country) {
      let missing = (country.leads + country.summary_postponed.leads) * country.crg / 100 - country.summary.ftds;
      let missed_amount = missing * country.payout;

      let final = parseFloat(country.adjustment) ? parseFloat(missed_amount) + parseFloat(country.adjustment) : parseFloat(missed_amount);
      let sign = parseFloat(country.adjustment) < 0 ? '-' : '+';
      let absAdjustment = country.adjustment ? Math.abs(country.adjustment) : 0;

      let cr = 0;
      if (country.leads + country.summary_postponed.leads > 0) {
        cr = country.summary.ftds / (country.leads + country.summary_postponed.leads) * 100;
      }

      country.cr = cr
      country.missing = missing
      country.missed_amount = missed_amount

      cr = Math.round(cr * 100) / 100
      missing = Math.round(missing * 100) / 100
      missed_amount = Math.round(missed_amount * 100) / 100
      final = Math.round(final * 100) / 100


      let main_text = `Leads: ${country.leads + country.summary_postponed.leads}, FTDs: ${country.summary.ftds}, CR: ${cr}%, CRG:${country.crg}%`;
      if (country.leads + country.summary_postponed.leads <= 0 || missing <= 0) {
        main_text += `, no missings`;
        return main_text
      }

      main_text += `, FTDs missing: ${missing}`;

      let amount_missing_text = `\n<strong><br>Amount missing: </strong> $${missing} * $${country.payout} = <strong>$${missed_amount}</strong>`;
      let final_missing_text = `\n<strong><br>Final missing: </strong> $${missed_amount} ${sign} $${absAdjustment} = <strong>$${final}</strong>`;

      main_text += amount_missing_text;
      if (absAdjustment) {
        main_text += final_missing_text;
        country.sign = sign
        country.absAdjustment = absAdjustment
      }

      // console.log(country);

      return main_text;
    },
    updateCountryLeads: function (country) {
      if (!country.leads) {
        country.leads = 0;
      }

      country.leads = parseInt(country.leads);

      if (country.leads < 0) {
        country.summary.cr = 0;
      } else if (country.ftds > 0) {
        country.summary.cr = 100 * country.summary.ftds / (country.leads + country.summary_postponed.leads);
        country.summary.cr = Math.round(country.summary.cr * 100) / 100
      }

      if (country.leads < 0) {
        this.$nextTick(() => {
          country.leads = 0;
          country.calculation = this.generateNewCalculation(country);
        });
        return;
      }

      if (country.leads >= country.total_leads) {
        this.$nextTick(() => {
          country.leads = country.total_leads;
          country.invalid_leads = 0;
          country.calculation = this.generateNewCalculation(country);

          // TODO: duplicated code
          if (country.missed_amount > 0 || this.dealStatus[country.broker_id][country.id].id != 2) {
            country.adjustment_disabled = false;
          }

        });
        return;
      }


      country.invalid_leads = country.total_leads - country.leads;
      country.calculation = this.generateNewCalculation(country);

      if (country.missed_amount > 0 || this.dealStatus[country.broker_id][country.id].id != 2) {
        country.adjustment_disabled = false;
      }

      if (country.missed_amount <= 0 || this.dealStatus[country.broker_id][country.id].id == 2) {
        country.adjustment_disabled = true;
        country.adjustment = 0;
      }
    }
  },

  filters: {
    formatNumber(num) {
      let dec = 2;
      let num_sign = num >= 0 ? 1 : -1;

      return parseFloat((Math.round((num * Math.pow(10, dec)) + (num_sign * 0.0001)) / Math.pow(10, dec)).toFixed(dec));
    },
    absValue(num) {
      return Math.abs(num);
    },
    sumFloat(a, b) {
      return parseFloat(a) + parseFloat(b);
    }
  },
}
</script>
